import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, PackageCheck } from "lucide-react";
import { api, apiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const today = () => new Date().toISOString().slice(0, 10);

/** Panels arriving on one calendar day (or all overdue) — confirm delivery in bulk. */
export function ArrivalDayDialog({
  projectId,
  date,
  overdue = false,
  overdueBefore,
  canConfirm = true,
  open,
  onOpenChange,
  onDone,
}) {
  const [panels, setPanels] = useState(null);
  const [excluded, setExcluded] = useState(new Set());
  const [deliveredOn, setDeliveredOn] = useState(today);
  const [reportMissing, setReportMissing] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPanels(null);
    setExcluded(new Set());
    setDeliveredOn(overdue ? today() : date || today());
    // Read-only viewers (e.g. Site) also see panels that already arrived.
    const params = { limit: 500 };
    if (canConfirm) params.status_logistics = "Shipped";
    if (overdue) params.estimated_arrival_before = overdueBefore;
    else params.estimated_arrival_date = date;
    api
      .get(`/projects/${projectId}/panels`, { params })
      .then((res) =>
        setPanels(
          canConfirm
            ? res.data.panels.filter((p) => p.statuses?.logistics !== "Arrived")
            : res.data.panels.filter((p) =>
                ["Shipped", "Arrived"].includes(p.statuses?.logistics)
              )
        )
      )
      .catch((error) => toast.error(apiError(error)));
  }, [open, date, overdue, overdueBefore, projectId, canConfirm]);

  const toggle = (uid) =>
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });

  const selected = (panels || []).filter((p) => !excluded.has(p.panel_uid));

  const markDelivered = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/projects/${projectId}/panels/delivery/bulk`, {
        panel_uids: selected.map((p) => p.panel_uid),
        delivered_on: deliveredOn,
        missing_panel_uids: reportMissing ? Array.from(excluded) : [],
      });
      if (data.failed?.length)
        toast.warning(`${data.updated.length} arrived · ${data.failed.length} failed`, {
          description: data.failed[0]?.detail,
        });
      else
        toast.success(
          `${data.updated.length} panel${data.updated.length === 1 ? "" : "s"} marked as Arrived`
        );
      if (data.missing_issue)
        toast.info(`Missing panels logged as ${data.missing_issue.issue_key}`);
      onOpenChange(false);
      onDone?.();
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setBusy(false);
    }
  };

  const groups = (panels || []).reduce((acc, panel) => {
    const key = panel.shipment_code || "No shipment code";
    acc[key] = acc[key] || { method: panel.shipping_method, panels: [] };
    acc[key].panels.push(panel);
    return acc;
  }, {});

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="arrival-day-dialog"
        className="max-w-lg border-slate-200 bg-white text-slate-800"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-lg font-medium tracking-tight">
            {overdue
              ? "Overdue shipments"
              : `Arrivals on ${date ? new Date(date).toLocaleDateString() : ""}`}
          </DialogTitle>
        </DialogHeader>

        {panels === null ? (
          <div className="grid h-24 place-items-center">
            <Loader2 size={18} className="animate-spin text-cyan-600" />
          </div>
        ) : panels.length === 0 ? (
          <p className="py-4 text-sm text-slate-500">
            {canConfirm
              ? "No panels are still in transit for this date."
              : "No shipments for this date."}
          </p>
        ) : (
          <>
            {canConfirm ? (
              <p className="text-[11.5px] text-slate-500">
                All panels are pre-selected. Click a panel to exclude it if it has not arrived —
                excluded panels stay <b>Shipped</b>.
              </p>
            ) : (
              <p className="text-[11.5px] text-slate-500" data-testid="arrival-readonly-note">
                Shipment details only — marking panels as <b>Arrived</b> requires the Logistics
                role.
              </p>
            )}
            <div className="okn-scroll max-h-64 space-y-3 overflow-y-auto pr-1">
              {Object.entries(groups).map(([code, group]) => (
                <div key={code} className="rounded-xl border border-slate-200 p-3">
                  <div className="flex items-center gap-2">
                    <span className="mono text-[12px] text-slate-900">{code}</span>
                    <span className="mono ml-auto text-[10.5px] text-slate-500">
                      {group.method || "—"} · {group.panels.length} panels
                    </span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {group.panels.map((panel) => {
                      const off = excluded.has(panel.panel_uid);
                      const arrived = panel.statuses?.logistics === "Arrived";
                      return (
                        <button
                          key={panel.panel_uid}
                          data-testid={`arrival-panel-${panel.panel_name}`}
                          onClick={() => canConfirm && toggle(panel.panel_uid)}
                          disabled={!canConfirm}
                          className={`mono rounded px-1.5 py-0.5 text-[10px] transition-colors ${
                            arrived
                              ? "bg-emerald-500/12 text-emerald-800 ring-1 ring-emerald-500/40"
                              : off
                                ? "bg-white text-slate-400 line-through ring-1 ring-slate-200"
                                : "bg-cyan-500/12 text-cyan-800 ring-1 ring-cyan-500/40"
                          }`}
                        >
                          {panel.panel_name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {canConfirm ? (
              <>
            <div className="flex items-center justify-between gap-3">
              <label className="mono text-[10.5px] uppercase tracking-wider text-slate-500">
                Actual delivery date
              </label>
              <input
                data-testid="arrival-delivered-on-input"
                type="date"
                value={deliveredOn}
                onChange={(e) => setDeliveredOn(e.target.value)}
                className="mono h-9 rounded-md border border-slate-200 bg-[#EEF2F8] px-2 text-xs text-slate-800"
              />
            </div>

            <div className="mono flex items-center justify-between text-[10.5px] text-slate-500">
              <span data-testid="arrival-selected-count">
                {selected.length} selected · {excluded.size} excluded
              </span>
              {excluded.size ? (
                <button
                  data-testid="arrival-reset-selection"
                  onClick={() => setExcluded(new Set())}
                  className="uppercase tracking-wider text-cyan-700 hover:text-cyan-600"
                >
                  Select all
                </button>
              ) : null}
            </div>

            {excluded.size ? (
              <label className="flex cursor-pointer items-center gap-2 text-[11.5px] text-slate-600">
                <input
                  data-testid="arrival-report-missing-checkbox"
                  type="checkbox"
                  checked={reportMissing}
                  onChange={(e) => setReportMissing(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 accent-cyan-600"
                />
                Open an issue for the {excluded.size} missing panel(s)
              </label>
            ) : null}

            <Button
              data-testid="mark-delivered-button"
              onClick={markDelivered}
              disabled={busy || selected.length === 0}
              className="mt-1 h-10 w-full rounded-full bg-cyan-600 text-[11px] uppercase tracking-wider text-white hover:bg-cyan-500 disabled:opacity-60"
            >
              {busy ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <>
                  <PackageCheck size={14} className="mr-2" />
                  Mark {selected.length} panel{selected.length === 1 ? "" : "s"} as Arrived
                </>
              )}
            </Button>
              </>
            ) : null}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
